import React, { useContext, useRef } from 'react';
import { Fade, Wrapper } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import ConfigurationProvider from './service/ConfigurationProvider';
import Context from './service/Context';
import SpecificationsUI from './SpecificationsUI';
import TrimCardsSlider from './TrimCardsSlider';
import useCompareAndTrimQuerystring from './hooks/useCompareAndTrimQuerystring';
import useSyncPaymentModels from './hooks/useSyncPaymentModels';
import { LegalDisclaimer } from './TrimSpecifications.styles';
import { getGtmCategoryProp, getHydratedTrims } from './service/utils';
import { getPriceTooltipLabelKey, getShowPriceFlags } from '../../utils/financeUtils';
import { styledCompiler } from '../../utils/markdown';
import { useLanguage, useProvinces, useUserOrDefaultProvince } from '../../utils/sitecoreContext';
import { variant1, variant2, variant3, variant4 } from './service/constants';
import { wrapJSSFields } from '../../utils/wrapJSSFields';
export const TrimSpecificationsUI = ({ tabsParams = {}, anchorId, legalDisclaimer, ctaLink, items, itemParentGroup, detModelKey, collapseLabel, expandLabel, gtmCategoryProp, componentName, }) => {
    const { t } = useTranslation();
    const language = useLanguage();
    const configurationProvider = useContext(Context);
    const { isDark, setSelectedTrim, setCurrentSlide, setModels, financial, addInteriorColorUpCharge, variant } = configurationProvider;
    const provinceCode = useUserOrDefaultProvince();
    const provinces = useProvinces();
    const msrpStartingFromLabel = t('Shared.Common.msrpStartingFromLabel');
    const sellingPriceLabel = t('Shared.Common.sellingPriceLabel');
    const specificationRefs = useRef([]);
    const { isInit, setIsInit, setActiveTab } = tabsParams;
    const trimKeysString = items.map(trim => trim.detKey.value).toString();
    const priceTooltipLabel = t(getPriceTooltipLabelKey(provinceCode, provinces));
    const { showMsrpPrice, showSellingPrice } = getShowPriceFlags(provinceCode, provinces);
    useCompareAndTrimQuerystring({
        trims: items,
        trimKeysString,
        isInit,
        setActiveTab,
        setIsInit,
        language,
        setSelectedTrim,
        setCurrentSlide,
    });
    useSyncPaymentModels({ items, itemParentGroup, detModelKey, setModels });
    const hydratedItems = getHydratedTrims(items, itemParentGroup, detModelKey, financial, msrpStartingFromLabel, sellingPriceLabel, priceTooltipLabel, showMsrpPrice, showSellingPrice, addInteriorColorUpCharge, variant);
    return (React.createElement(Fade, { id: anchorId === null || anchorId === void 0 ? void 0 : anchorId.value, "data-gtm-category": gtmCategoryProp, "data-gtm-component-type": componentName, initialOpacity: 0, duration: "t1", backgroundColor: isDark ? 'black' : 'white' },
        React.createElement(Wrapper, { pb: ['80vh', 'default'], gutters: [false, false] },
            React.createElement(TrimCardsSlider, { ctaLink: ctaLink, items: hydratedItems, specificationRefs: specificationRefs, componentName: componentName, gtmCategoryProp: gtmCategoryProp }),
            React.createElement(SpecificationsUI, { specificationRefs: specificationRefs, items: items, expandLabel: expandLabel, collapseLabel: collapseLabel, itemParentGroup: itemParentGroup }),
            React.createElement(LegalDisclaimer, { size: "legal", isDark: isDark }, styledCompiler(legalDisclaimer === null || legalDisclaimer === void 0 ? void 0 : legalDisclaimer.value)))));
};
const parseYearFields = (fields) => {
    var _a, _b, _c, _d, _e, _f;
    const { anchorId, legalDisclaimer, ctaLink, modelYear, collapseLabel, expandLabel } = fields;
    const trims = (_a = modelYear === null || modelYear === void 0 ? void 0 : modelYear.fields) === null || _a === void 0 ? void 0 : _a.trims;
    const year = (_c = (_b = modelYear === null || modelYear === void 0 ? void 0 : modelYear.fields) === null || _b === void 0 ? void 0 : _b.year) === null || _c === void 0 ? void 0 : _c.value;
    // TODO: Cleanup with a proper mapper
    // compulsory for auto
    const detModelKey = (_f = (_e = (_d = modelYear === null || modelYear === void 0 ? void 0 : modelYear.fields) === null || _d === void 0 ? void 0 : _d.model) === null || _e === void 0 ? void 0 : _e.detKey) === null || _f === void 0 ? void 0 : _f.value;
    return {
        anchorId,
        legalDisclaimer,
        ctaLink,
        items: trims,
        itemParentGroup: year,
        detModelKey,
        collapseLabel,
        expandLabel,
    };
};
const parseSubCategoryFields = (fields) => {
    var _a, _b, _c;
    const { anchorId, legalDisclaimer, ctaLink, collapseLabel, expandLabel } = fields;
    const subCategory = (_a = fields === null || fields === void 0 ? void 0 : fields.subCategory) === null || _a === void 0 ? void 0 : _a.fields;
    const trims = (_b = subCategory === null || subCategory === void 0 ? void 0 : subCategory.models) === null || _b === void 0 ? void 0 : _b.map(model => ({
        ...model,
    }));
    return {
        anchorId,
        legalDisclaimer,
        ctaLink,
        items: trims,
        itemParentGroup: (_c = subCategory === null || subCategory === void 0 ? void 0 : subCategory.name) !== null && _c !== void 0 ? _c : '',
        collapseLabel,
        expandLabel,
        detModelKey: undefined,
    };
};
const parserByVariant = Object.freeze({
    [variant1]: parseYearFields,
    [variant2]: parseYearFields,
    [variant3]: parseYearFields,
    [variant4]: parseSubCategoryFields,
});
const TrimSpecifications = ({ variant, vehicleType, appNameConfig, componentName, fields, tabsParams, }) => {
    const parseFields = parserByVariant[variant];
    if (!parseFields || typeof parseFields !== 'function')
        return null;
    const { anchorId, legalDisclaimer, ctaLink, detModelKey, collapseLabel, expandLabel, items, itemParentGroup } = parseFields(fields);
    const gtmCategoryProp = getGtmCategoryProp(wrapJSSFields(fields), variant);
    return (React.createElement(ConfigurationProvider, { variant: variant, vehicleType: vehicleType, appNameConfig: appNameConfig },
        React.createElement(TrimSpecificationsUI, { tabsParams: tabsParams, componentName: componentName, anchorId: anchorId, legalDisclaimer: legalDisclaimer, ctaLink: ctaLink, collapseLabel: collapseLabel, expandLabel: expandLabel, itemParentGroup: itemParentGroup, items: items || [], detModelKey: detModelKey, gtmCategoryProp: gtmCategoryProp })));
};
export default TrimSpecifications;
